import { useEffect } from "react";

const LOCK_CLASS = "tarh-submit-locked";
const AUTO_UNLOCK_DELAY_MS = 1500;

export default function SingleSubmitGuard() {
  useEffect(() => {
    const lockedForms = new Map();
    let activeRequests = 0;

    const getSubmitControls = (form, submitter) => {
      if (!form) return [];

      const controls = Array.from(
        form.querySelectorAll('button[type="submit"], input[type="submit"]')
      );

      if (submitter instanceof HTMLElement && !controls.includes(submitter)) {
        controls.push(submitter);
      }

      return controls;
    };

    const lockForm = (form, submitter) => {
      if (!form || lockedForms.has(form)) return;

      const controls = getSubmitControls(form, submitter);
      const snapshot = controls.map((control) => ({
        control,
        disabled: !!control.disabled,
      }));

      lockedForms.set(form, snapshot);
      form.dataset.submitLocked = "1";

      snapshot.forEach(({ control }) => {
        control.disabled = true;
        control.setAttribute("aria-disabled", "true");
        control.setAttribute("aria-busy", "true");
        control.classList.add(LOCK_CLASS);
      });

      window.setTimeout(() => {
        if (activeRequests === 0) {
          unlockForm(form);
        }
      }, AUTO_UNLOCK_DELAY_MS);
    };

    const unlockForm = (form) => {
      const snapshot = lockedForms.get(form);
      if (!snapshot) return;

      snapshot.forEach(({ control, disabled }) => {
        if (!control?.isConnected) return;
        control.disabled = disabled;
        control.removeAttribute("aria-disabled");
        control.removeAttribute("aria-busy");
        control.classList.remove(LOCK_CLASS);
      });

      delete form.dataset.submitLocked;
      lockedForms.delete(form);
    };

    const unlockAll = () => {
      Array.from(lockedForms.keys()).forEach((form) => unlockForm(form));
    };

    const handleSubmitCapture = (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      if (form.dataset.submitLocked === "1") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      lockForm(form, event.submitter);
    };

    const handleRequestState = (event) => {
      const count = Number(event?.detail?.activeCount ?? 0);
      activeRequests = Number.isFinite(count) ? Math.max(0, count) : 0;
      if (activeRequests === 0) {
        window.setTimeout(unlockAll, 200);
      }
    };

    document.addEventListener("submit", handleSubmitCapture, true);
    window.addEventListener("tarh:request-state", handleRequestState);

    return () => {
      document.removeEventListener("submit", handleSubmitCapture, true);
      window.removeEventListener("tarh:request-state", handleRequestState);
      unlockAll();
    };
  }, []);

  return null;
}
